import type { IContextEnrichmentChip } from "@/api/chat/chat.types";

/**
 * Assemble a first-person user-turn prompt from the user's chip selections
 * and free-text input on a context_enrichment card.
 *
 * Returns `""` when no non-whitespace chip label and no non-whitespace
 * free text is provided; callers should treat `""` as a validation
 * failure (e.g. leave the Refine button disabled).
 */
const ROLE_HINT_PREFIXES = ["as ", "for ", "from "] as const;
const TERMINAL_PUNCTUATION = new Set([".", "!", "?"]);

export function synthesizeRefinementPrompt({
    chips,
    freeText,
}: {
    chips: IContextEnrichmentChip[];
    freeText: string;
}): string {
    const parts: string[] = [];

    const chipLabels = chips
        .map((c) => c.label.trim())
        .filter((label) => label.length > 0);

    if (chipLabels.length > 0) {
        const firstLower = chipLabels[0].toLowerCase();
        const isRoleHint = ROLE_HINT_PREFIXES.some((p) =>
            firstLower.startsWith(p)
        );
        const opener = isRoleHint
            ? "I am asking "
            : "I want additional context: ";
        parts.push(opener + chipLabels.join(", "));
    }

    const freeTextClean = freeText.trim();
    if (freeTextClean.length > 0) {
        if (parts.length > 0) {
            parts.push(freeTextClean);
        } else {
            parts.push("I want to focus on " + freeTextClean);
        }
    }

    let out = parts.join(". ");
    if (out.length > 0 && !TERMINAL_PUNCTUATION.has(out[out.length - 1])) {
        out += ".";
    }
    return out;
}
